import { getStrapiToken } from '@/services/auth/getStrapiToken';
// @/services/app/store/modules/business-segments.js

/**
 * BusinessSegmentsHandler
 * 
 * Manages business segment operations for carbon accounting:
 * - API integration for retrieving segment data
 * - Real-time updates via WebSocket connection
 * - Segment answer management and structure preservation
 * - Segment lookup and cross-segment data import
 * - Data grouping by sector and country codes
 */
export class BusinessSegmentsHandler {
  /**
   * Initialize the business segments handler with a store reference
   * @param {Object} store - The main application store instance
   */
  constructor(store) {
    this.store = store;
    this.lastReloadTime = 0; // Track last reload to prevent rapid succession
    this.reloadDebounceMs = 2000; // 2 second debounce between reloads

    // Register WebSocket event handlers
    if (this.store.webSocketHandler) {
      this.store.webSocketHandler.registerHandler(
        'BusinessSegmentsHandler',
        this.handleWebSocketEvent.bind(this),
        ['business-segment:update', 'business-segment:populate', 'submission:complete'],
        this
      );
    }
  }

  /**
   * OPTIMIZED: Smart WebSocket handler with debouncing
   * Prevents redundant reloads during submission cascade
   * @param {Object} event - WebSocket event data
   * @returns {Promise<boolean>} Success status
   */
  async handleWebSocketEvent(event) {
    const now = Date.now();
    const timeSinceLastReload = now - this.lastReloadTime;

    // OPTIMIZATION: Debounce rapid successive events
    if (timeSinceLastReload < this.reloadDebounceMs) {
      return false;
    }

    // WebSocket event handling logging silenced

    this.lastReloadTime = now;
    
    // OPTIMIZATION: Use non-forced load for WebSocket events
    // The backend already updated the database, we just need to fetch
    await this.loadBusinessSegments(false);
    
    // Operation Lightsaber: Mark suppliers as stale when business segments update
    if (event.type === 'business-segment:update' && this.store.supplychainSuppliersHandler) {
      this.store.supplychainSuppliersHandler.markStale('Business segments updated');
    }
    
    return true;
  }

  /**
   * Load business segments data from API
   * @param {boolean} force - Force refresh and populate if needed
   * @returns {Promise<Object>} Segments grouped by sector
   * @throws {Error} If API request fails or no project ID is available
   */
  async loadBusinessSegments(force = false) {
    try {
      this.store.state.loading.businessSegments = true;
      const projectId = this.store.getProjectId();

      if (!projectId) {
        throw new Error('No project ID available');
      }

      // ✅ CRITICAL FIX: ONLY populate when explicitly requested with force=true
      // NEVER auto-populate during routine refreshes, even if store is empty
      // Populate happens ONCE at login in _loadCriticalData with force=true
      if (force) {
        try {
          const populateUrl = `${process.env.GATSBY_STRAPI_URL}/api/business-segments/project/${projectId}/populate`;
          const populateResponse = await fetch(populateUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${getStrapiToken()}`
            }
          });

          if (!populateResponse.ok) {
            console.error('Failed to force populate business segments:', populateResponse.status);
          }
          // Force populate result logging silenced
        } catch (populateError) {
          console.error('Error during forced populate:', populateError);
          // Continue with regular load even if populate fails
        }
      }
      // ✅ REMOVED: Auto-populate logic that triggered during refreshes
      // The old logic checked if store was empty and auto-populated, causing
      // 40-second delays during submission and route changes
      // Now: populate ONLY happens at login with force=true

      // Now load the data (either existing or newly populated)
      const url = `${process.env.GATSBY_STRAPI_URL}/api/business-segments/project/${projectId}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${getStrapiToken()}` },
        cache: 'no-cache'  // PREVENT STALE DATA
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch business segments: ${response.status}`);
      }

      // Store raw data without complex transformations
      const { data } = await response.json();

      // Group by sector with minimal processing
      const bySector = {};

      if (data && data.length > 0) {
        data.forEach(item => {
          const segment = item.attributes || item;
          const sectorCode = segment.sectorCode;

          if (!sectorCode) return;

          // Initialize array if needed
          if (!bySector[sectorCode]) {
            bySector[sectorCode] = [];
          }

          // Just add the segment with minimal processing
          bySector[sectorCode].push({
            id: item.id,
            ...segment
          });
        });
      }

      // Update store with simple structure
      this.store.state.data.businessSegments = {
        bySector,
        meta: {
          lastSync: new Date().toISOString()
        }
      };

      // Notify subscribers
      this.store.notifySubscribers('businessSegments');

      // Persist to sessionStorage so CMD+R doesn't lose real-time updates.
      // Was missing — unlike customerRequests which calls saveState() after loading,
      // business segments were only in memory and lost on reload.
      if (this.store.saveState) {
        await this.store.saveState();
      }

      return bySector;
    } catch (error) {
      console.error('Error loading business segments:', error);
      this.store.handleError('businessSegments', error);
      throw error;
    } finally {
      this.store.state.loading.businessSegments = false;
    }
  }

  /**
   * Get a segment by ID from all available sectors
   * @param {string|number} segmentId - Segment ID to search for
   * @returns {Object|null} Segment object or null if not found
   */
  getSegmentById(segmentId) {
    const segments = this.store.state.data.businessSegments;
    if (!segments?.bySector) return null;

    // Search through all sectors
    for (const sector in segments.bySector) {
      const segment = segments.bySector[sector].find(s => s.id === segmentId);
      if (segment) return segment;
    }

    return null;
  }

  /**
   * Update segment answers while preserving complete data structure
   * @param {string|number} segmentId - Segment ID to update
   * @param {Object} completeAnswers - Complete answers structure
   * @param {Array} [completeAnswers.BusinessSegmentAnswers] - Regular answers
   * @param {Array} [completeAnswers.BusinessSegmentChoiceAnswers] - Choice answers
   * @param {Array} [completeAnswers.BusinessSegmentHotspotAnswers] - Hotspot answers
   * @returns {Promise<Object>} Updated segment data
   * @throws {Error} If segment not found or API request fails
   */
  async updateAnswers(segmentId, completeAnswers) {
    try {
      const projectId = this.store.getProjectId();
      if (!projectId) {
        throw new Error('No project ID available');
      }

      // Find segment
      const segment = this.getSegmentById(segmentId);
      if (!segment) {
        throw new Error('Segment not found');
      }

      // CRITICAL FIX: Verify complete structure has all required arrays
      if (!completeAnswers.BusinessSegmentAnswers) completeAnswers.BusinessSegmentAnswers = [];
      if (!completeAnswers.BusinessSegmentChoiceAnswers) completeAnswers.BusinessSegmentChoiceAnswers = [];
      if (!completeAnswers.BusinessSegmentHotspotAnswers) completeAnswers.BusinessSegmentHotspotAnswers = [];

      // Create payload with complete answers
      const payload = {
        sectorCode: segment.sectorCode,
        countryCode: segment.countryCode,
        answers: completeAnswers
      };

      // Payload structure logging silenced

      // Direct API call
      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/business-segments/project/${projectId}/save-and-calc`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getStrapiToken()}`
          },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Save failed (${response.status}):`, errorText);
        throw new Error(`Save failed: ${response.status} - ${errorText}`);
      }

      // Get response and reload data
      const result = await response.json();

      // Backend wraps saveAndCalc in withHeartbeat for Heroku H12
      // mitigation. Errors that occur after streaming starts (>25s)
      // come back as HTTP 200 with `{error:...}` body, so a status-
      // only check would silently treat them as success and return
      // stale data. Detect the body-error and throw.
      if (result?.error) {
        const message = result.error.message || 'Save failed';
        console.error('Save failed (body error after stream-flip):', message);
        throw new Error(message);
      }

      await this.loadBusinessSegments();
      
      // Operation Lightsaber: Mark suppliers as stale (performance scores changed)
      if (this.store.supplychainSuppliersHandler) {
        this.store.supplychainSuppliersHandler.markStale('Self-assessment updated');
      }

      return result.data;
    } catch (error) {
      console.error('Error updating answers:', error);
      throw error;
    }
  }

  /**
   * Import answers from one segment to another with the same sector code
   * @param {number} projectId - Project ID
   * @param {string} sectorCode - Sector code (must be the same for source and target)
   * @param {string} sourceCountryCode - Source country code to import from
   * @param {string} targetCountryCode - Target country code to import to
   * @returns {Promise<Object>} Import result
   * @throws {Error} If missing parameters or API request fails
   */
  async importAnswers(projectId, sectorCode, sourceCountryCode, targetCountryCode) {
    try {
      if (!projectId || !sectorCode || !sourceCountryCode || !targetCountryCode) {
        throw new Error('Missing required parameters');
      }

      if (process.env.NODE_ENV === 'development') {
        console.log(`Importing answers from ${sectorCode}/${sourceCountryCode} to ${sectorCode}/${targetCountryCode}`);
      }

      // Direct API call
      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/business-segments/project/${projectId}/import-answers`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getStrapiToken()}`
          },
          body: JSON.stringify({
            sourceSectorCode: sectorCode,
            sourceCountryCode,
            targetCountryCode
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Import failed: ${response.status}`);
      }

      // Parse response and reload data
      const result = await response.json();
      await this.loadBusinessSegments();

      return result;
    } catch (error) {
      console.error('Error importing answers:', error);
      throw error;
    }
  }

  /**
   * Load benchmarks for a specific sector
   * @param {string} sectorCode - Sector code to get benchmarks for
   * @returns {Promise<Object>} Benchmark data
   */
  async loadBenchmarks(sectorCode) {
    try {
      if (!sectorCode) {
        throw new Error('Sector code is required');
      }

      if (process.env.NODE_ENV === 'development') {
        console.log(`Loading benchmarks for sector: ${sectorCode}`);
      }

      // Simple API call to get benchmarks
      const url = `${process.env.GATSBY_STRAPI_URL}/api/business-segments/benchmarks/${sectorCode}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${getStrapiToken()}` }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch benchmarks: ${response.status}`);
      }

      const { data } = await response.json();

      // Store benchmark data in the store
      if (!this.store.state.data.benchmarks) {
        this.store.state.data.benchmarks = {};
      }
      this.store.state.data.benchmarks[sectorCode] = data;

      // Notify subscribers
      this.store.notifySubscribers('benchmarks');

      return data;
    } catch (error) {
      console.error(`Error loading benchmarks for ${sectorCode}:`, error);
      this.store.handleError('benchmarks', error);
      return null;
    }
  }


}